import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { Row } from 'react-table';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { DataTable } from 'src/common/components/table/data-table';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderOrderType,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { Pagination } from 'src/common/Pagination';
import { useCurrentOrganization } from 'src/store/organization';
import { Nullable } from 'src/types/nullable.type';
import { formatDuration } from 'src/utils/date';
import { apiGetAllJourneysInsights } from 'src/utils/journeyApi';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { useDepsMatchingCallbackRef } from 'src/utils/react/deps-matching-callback-ref.hook';
import { formatNumber } from 'src/utils/text';
import { JourneyMetrics } from '../constants';
import isNull from 'lodash/isNull';
import { Checkbox } from 'src/common/form/checkbox';
import { Journey } from 'src/common/interfaces/journey.interface';
import { JourneyDownloadVisitor } from './journey/download-visitors-csv';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

export type InsightsJourneysListHeaderTypes =
  | 'name'
  | 'steps'
  | 'views'
  | 'average_time'
  | 'average_completion_percentage';

export type InsightsJourneysListHeaderSortType = TableSortableColumnHeaderSortType<InsightsJourneysListHeaderTypes>;

export const DashboardInsightsJourneysAll = ({
  onSelectionChange,
  journeyIdsToDownloadVisitors,
}: {
  journeyIdsToDownloadVisitors: JourneyDownloadVisitor;
  onSelectionChange: (payload: JourneyDownloadVisitor) => void;
}) => {
  const history = useHistory();
  const [journeysInsights, setJourneysInsights] = useState<any[]>([]);
  const [loading, setLoading] = useState<Nullable<boolean>>(null);
  const [pages, setPages] = useState(1);

  const { currentOrganization } = useCurrentOrganization();
  const { features, showUpgradeModal } = useFeatureRestrictions();

  const {
    filterBarParams: {
      search_term,
      page,
      sort_by,
      sort_order,
      filters: { tag_ids },
    },
    updatePage,
    updateSort,
  } = useFilterBarParams();

  const onFetchJourneysInsightsRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setJourneysInsights(response.insights);
      setPages(response.pages);
      setLoading(false);
    },
    [currentOrganization.id, search_term, page, sort_by, sort_order, tag_ids]
  );

  const showLoadingSpinner = loading || isNull(loading);

  const fetchJourneysInsights = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page,
        ...(search_term && { search_str: search_term }),
        ...(sort_by && { sort_by }),
        ...(sort_by && sort_order !== 'default' && { sort_order }),
      };
      if (tag_ids?.length > 0) {
        params.tag_ids = tag_ids.filter(Boolean).join(',');
      }
      const response = await apiGetAllJourneysInsights(currentOrganization.id, params);
      onFetchJourneysInsightsRef.current &&
        onFetchJourneysInsightsRef.current(response, [
          currentOrganization.id,
          search_term,
          page,
          sort_by,
          sort_order,
          tag_ids,
        ]);
    } catch (e) {
      console.error(e);
    }
  }, [onFetchJourneysInsightsRef, currentOrganization.id, search_term, tag_ids, page, sort_by, sort_order]);

  useEffect(() => {
    fetchJourneysInsights();
  }, [fetchJourneysInsights]);

  const onRowClicked = (row: Row) => {
    const { journey } = row.original as any;
    
    // Check if user can access basic insights (available on all plans)
    if (!features.canAccessBasicInsights) {
      showUpgradeModal(
        'basic_insights',
        'You need access to basic insights to view journey analytics. Please contact support.',
        'Insights Access Required'
      );
      return;
    }
    
    history.push(`/${currentOrganization.slug}/insights/journeys/${journey.uuid}`);
  };

  const onTableHeaderClick = (sortStack: InsightsJourneysListHeaderSortType) => {
    updateSort(sortStack.sortName, sortStack.orderBy);
  };

  const selectedRowsLength = useMemo(
    () => Object.values(journeyIdsToDownloadVisitors).length,
    [journeyIdsToDownloadVisitors]
  );

  const toggleRowSelection = (journeyId: Journey['uuid'], journeyName: Journey['name']) => {
    const checkedJourney = journeyIdsToDownloadVisitors[journeyId];
    const newSelections = {
      ...journeyIdsToDownloadVisitors,
      [journeyId]: {
        enable: !checkedJourney?.enable,
        name: journeyName,
      },
    };
    onSelectionChange(newSelections);
  };

  const handleHeaderSelectAllRowsChange = (journeys: Journey[]) => {
    const allSelected = journeys.every(({ uuid }) => journeyIdsToDownloadVisitors[uuid]?.enable);
    const newSelections = journeys.reduce((acc, { uuid, name }) => {
      acc[uuid] = {
        enable: !allSelected,
        name,
      };
      return acc;
    }, {} as JourneyDownloadVisitor);
    onSelectionChange(newSelections);
  };

  const columns = useMemo(
    () => [
      generateColumn({
        Header: ({ data }: { data: any }) => {
          const journeysWithSession = data.filter(({ num_sessions }: { num_sessions: number }) => !!num_sessions);
          return (
            <Checkbox
              isNeue
              onClick={(e) => {
                e.stopPropagation();
              }}
              disabled={!journeysWithSession.length}
              checked={!!journeysWithSession.length && selectedRowsLength === journeysWithSession.length}
              onChange={() => {
                handleHeaderSelectAllRowsChange(journeysWithSession.map((d: any) => d.journey));
              }}
            />
          );
        },
        accessor: 'select',
        width: 40,
        Cell: (data: any) => {
          const checkedJourney = journeyIdsToDownloadVisitors[data.row.original.journey.uuid];
          const isJourneyChecked = !!checkedJourney && checkedJourney.enable;
          return (
            <Checkbox
              isNeue
              onClick={(e) => {
                e.stopPropagation();
              }}
              disabled={!data.row.original.num_sessions}
              checked={isJourneyChecked}
              onChange={() => {
                const { uuid: journeyId, name } = data.row.original.journey;
                toggleRowSelection(journeyId, name);
              }}
            />
          );
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'name'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='name'
            onClick={onTableHeaderClick}
            title='Name'
            tooltip='Journey name'
          />
        ),
        accessor: 'name',
        width: 462,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => (
          <span className='line-clamp-2 lg:line-clamp-none'>{row.original.journey.name}</span>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'steps'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='steps'
            onClick={onTableHeaderClick}
            title='Steps'
            tooltip='Number of steps in Journey'
          />
        ),
        accessor: 'steps',
        width: 205.5,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return formatNumber(row.original.num_of_steps);
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'views'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='views'
            onClick={onTableHeaderClick}
            title='Views'
            tooltip='Total views of Journey'
          />
        ),
        accessor: 'views',
        width: 205.5,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return formatNumber((row.original as JourneyMetrics).num_sessions || 0);
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'average_time'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='average_time'
            onClick={onTableHeaderClick}
            title='Average time'
            tooltip='Average time spent in this Journey'
          />
        ),
        accessor: 'average_time',
        width: 205.5,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const average_time = row.original.average_time || 0;
          return average_time ? formatDuration(average_time) : '-';
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'average_completion_percentage'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='average_completion_percentage'
            onClick={onTableHeaderClick}
            title='Average completion'
            tooltip='Average number of steps viewed in this Journey'
          />
        ),
        accessor: 'average_completion_percentage',
        width: 205.5,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const { average_completion_percentage } = row.original as JourneyMetrics;
          return average_completion_percentage ? `${average_completion_percentage}%` : '-';
        },
      }),
    ],
    [sort_by, sort_order, journeyIdsToDownloadVisitors]
  );

  const onPageChange = useCallback((e: any, newPage: number) => {
    updatePage(newPage);
  }, []);

  const renderContent = () => {
    if (showLoadingSpinner) {
      return <FixedLoadingSpinner />;
    }

    if (journeysInsights.length > 0) {
      return <DataTable columns={columns} data={journeysInsights} onRowClick={onRowClicked} />;
    }

    if (search_term) {
      return <div className='text-bedrock-p text-bedrock-black'>No journeys found</div>;
    }

    return (
      <div className='max-w-[640px] text-bedrock-p text-bedrock-black'>
        Create some journeys to see their insights here
      </div>
    );
  };

  return (
    <div className='flex flex-col'>
      <Helmet>
        <title>Journey | Insights | Journey</title>
      </Helmet>
      {renderContent()}
      {pages > 1 && (
        <div className='mt-6'>
          <Pagination count={pages} page={page} onChange={onPageChange} />
        </div>
      )}
    </div>
  );
};
